import React, { useEffect, useState } from 'react'
import { useGetUniversityQuery, useGetCurrenciesQuery, useGetEducationLevelsQuery } from '../../store/api';
import { PencilIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { Audio } from 'react-loader-spinner'

function UniversityViewModal({
    setIsViewModalOpen,
    viewingUniversity,
    handleEdit
}) {

    const [activeTab, setActiveTab] = useState('details');

    const [data, setData] = useState([])
    const [programs, setPrograms] = useState([])
    const [scholarships, setScholarships] = useState([])
    const [applications, setApplications] = useState([])

    const { data: response, isLoading, isSuccess } = useGetUniversityQuery(viewingUniversity.id)
    const { data: currenciesData } = useGetCurrenciesQuery()
    const { data: educationLevelsData } = useGetEducationLevelsQuery()

    // Extract actual data arrays from API responses
    const currencies = currenciesData?.data || currenciesData || [];
    const educationLevels = educationLevelsData?.data || educationLevelsData || [];

    // Function to map currency IDs to codes
    const getCurrencyCode = (currencyId) => {
        if (!currenciesData || !currencyId) return currencyId
        
        // Handle different data structures
        const currencies = currenciesData.data || currenciesData
        if (!Array.isArray(currencies)) return currencyId
        
        const currency = currencies.find(curr => curr.id && curr.id.toString() === currencyId.toString())
        return currency ? currency.code : currencyId
    }

    // Helper function to get degree level names from IDs
    const getDegreeLevelNames = (degreeLevelIds) => {
        if (!degreeLevelIds || !educationLevels) return '';
        
        const ids = typeof degreeLevelIds === 'string' ? 
            degreeLevelIds.split(',').map(id => id.trim()) : 
            degreeLevelIds;
        
        const names = ids.map(id => {
            const level = educationLevels.find(l => l.id && l.id.toString() === id.toString());
            return level ? level.name : id;
        }).filter(Boolean);
        
        return names.join(', ');
    };

    useEffect(() => {
        if (!isLoading && response) {
            setData(response.data)
            setPrograms(response.data.programs || [])
            setScholarships(response.data.scholarships || [])

            const DATA = []
            if (response.data.applications && Array.isArray(response.data.applications)) {
                response.data.applications.forEach(item => {
                    if (item?.student) {
                        DATA.push(item.student)
                    }
                });
            }
            setApplications(DATA)
        }
    }, [response, isLoading, isSuccess, currenciesData])

    if (isLoading) {
        return (
            <div className='fixed inset-0 flex items-center bg-white opacity-[.5] justify-center'>
                <Audio
                    height="80"
                    width="80"
                    radius="9"
                    color="green"
                    ariaLabel="loading"
                    wrapperStyle
                    wrapperClass
                />
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsViewModalOpen(false)} />

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full sm:p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className={`h-3 w-3 rounded-full ${viewingUniversity.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <h3 className="text-lg font-medium text-gray-900">
                                {viewingUniversity.name} - Ətraflı Məlumat
                            </h3>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                #{viewingUniversity.ranking || 'N/A'} Reytinq
                            </span>
                        </div>
                        <button
                            onClick={() => setIsViewModalOpen(false)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <span className="text-2xl">&times;</span>
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200 mb-6">
                        <nav className="-mb-px flex space-x-8">
                            <button
                                onClick={() => setActiveTab('details')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'details'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Universitet Təfərrüatları
                            </button>
                            <button
                                onClick={() => setActiveTab('programs')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'programs'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Ixtisaslar ({programs.length})
                            </button>
                            <button
                                onClick={() => setActiveTab('scholarships')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'scholarships'
                                    ? 'border-emerald-500 text-emerald-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Təqaüd Proqramları ({scholarships.length})
                            </button>
                            <button
                                onClick={() => setActiveTab('applications')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'applications'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Müraciətlər ({applications.length})
                            </button>
                        </nav>
                    </div>

                    {/* Tab Content */}
                    
                    {activeTab === 'details' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-6">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-blue-900 mb-3">Əsas Məlumatlar</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Ölkə:</span>
                                            <span className="font-medium">{data.country?.name || viewingUniversity.country?.name || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Şəhər:</span>
                                            <span className="font-medium">{data.city?.name || viewingUniversity.city?.name || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Reytinq:</span>
                                            <span className="font-medium">#{data.ranking || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Status:</span>
                                            <span className={`px-2 py-1 rounded-full text-xs ${data.is_active 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-red-100 text-red-800'
                                            }`}>
                                                {data.is_active ? 'Aktiv' : 'Deaktiv'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-green-900 mb-3">Əlaqə Məlumatları</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Nümayəndələr:</span>
                                            <span className="font-medium">{data.representatives || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Focal Point:</span>
                                            <span className="font-medium">{data.focal_point || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Email:</span>
                                            <span className="font-medium">{data.email || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Telefon:</span>
                                            <span className="font-medium">{data.phone || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Vebsayt:</span>
                                            <span className="font-medium">
                                                {data.website ? (
                                                    <a href={data.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                        {data.website}
                                                    </a>
                                                ) : 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Ünvan:</span>
                                            <div className="mt-1 text-gray-900 bg-white p-2 rounded border text-xs">
                                                {data.address || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-6">
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-purple-900 mb-3">Akademik Məlumatlar</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Ortalama Xərc:</span>
                                            <span className="font-medium">{data.average_cost || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Son Tarix:</span>
                                            <span className="font-medium">{data.submission_deadline || 'N/A'}</span>
                                        </div>

                                    </div>
                                </div>

                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-orange-900 mb-3">Əlavə Məlumatlar</h4>
                                    <div className="space-y-3 text-sm">
                                        <div>
                                            <span className="text-gray-600 font-medium">Tələb Olunan Sənədlər:</span>
                                            <div className="mt-1 text-gray-900 bg-white p-3 rounded border">
                                                {data.required_documents || 'Məlumat yoxdur'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-gray-600 font-medium">Əlavə Şərhlər:</span>
                                            <div className="mt-1 text-gray-900 bg-white p-3 rounded border">
                                                {data.comments || 'Məlumat yoxdur'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'programs' && (
                        <div className="space-y-3">
                            {programs.length > 0 ? (
                                programs.map((program, index) => (
                                    <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <div className={`h-3 w-3 rounded-full ${program.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                                                    <div className="font-medium text-gray-900">{program.name}</div>
                                                </div>
                                                <div className="text-sm text-gray-600 mb-2">
                                                    {program.duration && <span>Müddət: {program.duration} | </span>}
                                                    <span className={`${program.is_active ? 'text-green-600' : 'text-red-600'}`}>
                                                        {program.is_active ? 'Aktiv' : 'Deaktiv'}
                                                    </span>
                                                </div>
                                                {program.description && (
                                                    <p className="text-sm text-gray-600 mt-2">{program.description}</p>
                                                )}
                                            </div>
                                            <div className="text-right ml-4">
                                                {program.tuition_amount && program.tuition_currency && (
                                                    <>
                                                        <div className="text-lg font-medium text-blue-600">
                                                            {program.tuition_amount} {getCurrencyCode(program.tuition_currency)}
                                                        </div>
                                                        <div className="text-xs text-gray-500">İllik</div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8">
                                    <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                        <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                    </div>
                                    <h5 className="text-lg font-medium text-gray-900 mb-2">Proqram tapılmadı</h5>
                                    <p className="text-gray-500">Bu universitətdə hələ proqram qeydiyyatda deyil</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'scholarships' && (
                        <div className="space-y-4">
                            {scholarships.length > 0 ? (
                                scholarships.map((scholarship, index) => (
                                    <div key={index} className="bg-gradient-to-r from-emerald-50 to-emerald-100 p-6 rounded-lg border border-emerald-200 hover:border-emerald-300 transition-colors">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <div className={`h-3 w-3 rounded-full ${scholarship.is_active ? 'bg-emerald-500' : 'bg-gray-400'}`}></div>
                                                    <div className="text-lg font-semibold text-emerald-900">{scholarship.name}</div>
                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                        scholarship.coverage_type === 'Tam təqaüd' 
                                                            ? 'bg-green-100 text-green-800 border border-green-200' 
                                                            : 'bg-blue-100 text-blue-800 border border-blue-200'
                                                    }`}>
                                                        {scholarship.coverage_type || 'Təqaüd'}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-emerald-700 mb-3">{scholarship.description}</p>
                                                
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                                    <div className="bg-white p-3 rounded-lg border border-emerald-100">
                                                        <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Sahə</div>
                                                        <div className="text-sm font-semibold text-gray-900">{scholarship.field_of_study}</div>
                                                    </div>
                                                    <div className="bg-white p-3 rounded-lg border border-emerald-100">
                                                        <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Dərəcə</div>
                                                        <div className="text-sm font-semibold text-gray-900">{getDegreeLevelNames(scholarship.degree_level)}</div>
                                                    </div>
                                                    <div className="bg-white p-3 rounded-lg border border-emerald-100">
                                                        <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Məbləğ</div>
                                                        <div className="text-sm font-semibold text-gray-900">
                                                            {scholarship.amount} {scholarship.currency_code || getCurrencyCode(scholarship.currency_id)}
                                                        </div>
                                                        {scholarship.amount_type && (
                                                            <div className="text-xs text-gray-500 mt-1">{scholarship.amount_type}</div>
                                                        )}
                                                    </div>
                                                    <div className="bg-white p-3 rounded-lg border border-emerald-100">
                                                        <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Müddət</div>
                                                        <div className="text-sm font-semibold text-gray-900">{scholarship.duration}</div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                    <div className="bg-white p-3 rounded-lg border border-emerald-100">
                                                        <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Son Müraciət Tarixi</div>
                                                        <div className="text-sm font-semibold text-gray-900">
                                                            {scholarship.application_deadline ? new Date(scholarship.application_deadline).toLocaleDateString('az-AZ') : 'Tarix yoxdur'}
                                                        </div>
                                                    </div>
                                                    <div className="bg-white p-3 rounded-lg border border-emerald-100">
                                                        <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Status</div>
                                                        <div className={`text-sm font-semibold ${scholarship.is_active ? 'text-emerald-600' : 'text-red-600'}`}>
                                                            {scholarship.is_active ? 'Aktiv' : 'Deaktiv'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {scholarship.requirements && (
                                                    <div className="bg-white p-3 rounded-lg border border-emerald-100 mb-3">
                                                        <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-2">Tələb Olunan Sənədlər</div>
                                                        <div className="text-sm text-gray-900">{scholarship.requirements}</div>
                                                    </div>
                                                )}

                                                {scholarship.application_process && (
                                                    <div className="bg-white p-3 rounded-lg border border-emerald-100 mb-3">
                                                        <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-2">Müraciət Prosesi</div>
                                                        <div className="text-sm text-gray-900">{scholarship.application_process}</div>
                                                    </div>
                                                )}

                                                {scholarship.additional_benefits && (
                                                    <div className="bg-white p-3 rounded-lg border border-emerald-100 mb-3">
                                                        <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-2">Əlavə Faydalar</div>
                                                        <div className="text-sm text-gray-900">{scholarship.additional_benefits}</div>
                                                    </div>
                                                )}

                                                <div className="flex items-center space-x-4 pt-3 border-t border-emerald-200">
                                                    {scholarship.contact_email && (
                                                        <a 
                                                            href={`mailto:${scholarship.contact_email}`}
                                                            className="inline-flex items-center text-sm text-emerald-600 hover:text-emerald-800 hover:underline"
                                                        >
                                                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                            </svg>
                                                            {scholarship.contact_email}
                                                        </a>
                                                    )}
                                                    {scholarship.website && (
                                                        <a 
                                                            href={scholarship.website} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center text-sm text-emerald-600 hover:text-emerald-800 hover:underline"
                                                        >
                                                            <GlobeAltIcon className="h-4 w-4 mr-1" />
                                                            Vebsayt
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8">
                                    <div className="mx-auto h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                                        <svg className="h-6 w-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                        </svg>
                                    </div>
                                    <h5 className="text-lg font-medium text-gray-900 mb-2">Təqaüd tapılmadı</h5>
                                    <p className="text-gray-500">Bu universitətdə hələ təqaüd proqramı qeydiyyatda deyil</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'applications' && (
                        <div className="space-y-3">
                            {applications.length > 0 ? (
                                applications.map((student) => (
                                    <div key={student.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-green-300 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                                                    <span className="text-xs font-bold text-white">
                                                        {student.first_name?.[0] || ''}{student.last_name?.[0] || ''}
                                                    </span>
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900">
                                                        {student.first_name || ''} {student.last_name || ''}
                                                    </div>
                                                    <div className="text-sm text-gray-500">{student.phone || 'N/A'}</div>
                                                    <div className="text-xs text-gray-400">
                                                        Proqramlar: {student.applied_programs || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs text-green-600 font-medium">
                                                    Müraciət tarixi: {student.created_at ? new Date(student.created_at).toLocaleDateString("az-AZ") : 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) :
                                <div className="text-center py-8">
                                    <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                        <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                    <h5 className="text-lg font-medium text-gray-900 mb-2">Müraciət tapılmadı</h5>
                                    <p className="text-gray-500">Bu universitetə hələ müraciət edilməyib</p>
                                </div>
                            }
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={() => {
                                setIsViewModalOpen(false);
                                handleEdit(viewingUniversity);
                            }}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
                            Redaktə Et
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UniversityViewModal 